import { useState } from 'react';
import { Plus, X, Filter } from 'lucide-react';
import type { FilterCondition, FilterState } from '../filter-panel';

type TargetGroup = 'include' | 'exclude';

interface TimelineFilterBarProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
}

function fieldLabel(field: FilterCondition['field']): string {
  return field === 'sender' ? 'Avsender' : 'Tekst';
}

function groupLabel(group: TargetGroup): string {
  return group === 'include' ? 'Inkluder' : 'Ekskluder';
}

function groupHint(group: TargetGroup): string {
  return group === 'include' ? 'Alle må matche' : 'Ingen kan matche';
}

export function TimelineFilterBar({ filters, onChange }: TimelineFilterBarProps) {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [targetGroup, setTargetGroup] = useState<TargetGroup>('include');
  const [field, setField] = useState<FilterCondition['field']>('sender');
  const [value, setValue] = useState('');

  const hasConditions = filters.include.length > 0 || filters.exclude.length > 0;
  const nextValue = value.trim();

  const groupedConditions = [
    { key: 'include' as const, conditions: filters.include },
    { key: 'exclude' as const, conditions: filters.exclude },
  ] as const;

  function addCondition() {
    if (!nextValue) return;
    const nextCondition = { field, value: nextValue };
    const current = filters[targetGroup];
    if (
      current.some(
        (item) => item.field === nextCondition.field && item.value === nextCondition.value,
      )
    )
      return;
    onChange({ ...filters, [targetGroup]: [...current, nextCondition] });
    setValue('');
    setIsEditorOpen(false);
  }

  function removeCondition(group: TargetGroup, index: number) {
    const nextGroup = filters[group].filter((_, currentIndex) => currentIndex !== index);
    onChange({ ...filters, [group]: nextGroup });
  }

  return (
    <div
      className="relative border-b px-4 py-2.5"
      style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'var(--color-surface-raised)' }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-text-tertiary">
          <Filter size={12} />
          <span className="text-[11px] font-semibold uppercase tracking-wider">Søkefilter</span>
        </div>

        <div className="h-3.5 w-px" style={{ background: 'rgba(255,255,255,0.12)' }} />

        {groupedConditions.map(({ key, conditions }) => (
          <ConditionGroup
            key={key}
            group={key}
            conditions={conditions}
            onRemove={(index) => removeCondition(key, index)}
          />
        ))}

        {!hasConditions && (
          <span className="text-[11px] text-text-secondary">
            Filtrer på avsender eller tekst på tvers av alle hendelser
          </span>
        )}

        <button
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-semibold transition-colors"
          style={{
            background: isEditorOpen ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.12)',
            border: '1px solid rgba(99,102,241,0.4)',
            color: '#a5b4fc',
          }}
          onClick={() => setIsEditorOpen((open) => !open)}
          aria-expanded={isEditorOpen}
        >
          <Plus size={12} />
          Filter
        </button>
      </div>

      {isEditorOpen && (
        <FilterEditor
          targetGroup={targetGroup}
          onGroupChange={setTargetGroup}
          field={field}
          onFieldChange={setField}
          value={value}
          onValueChange={setValue}
          onAdd={addCondition}
          canAdd={Boolean(nextValue)}
        />
      )}
    </div>
  );
}

interface ConditionGroupProps {
  group: TargetGroup;
  conditions: FilterCondition[];
  onRemove: (index: number) => void;
}

function ConditionGroup({ group, conditions, onRemove }: ConditionGroupProps) {
  if (conditions.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className="text-[11px] font-semibold"
        style={{ color: group === 'include' ? '#93c5fd' : '#fca5a5' }}
      >
        {groupLabel(group)}:
      </span>
      {conditions.map((condition, index) => (
        <span
          key={`${condition.field}-${condition.value}-${index}`}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium"
          style={{
            borderColor: group === 'include' ? 'rgba(58,130,246,0.5)' : 'rgba(239,68,68,0.5)',
            background: group === 'include' ? 'rgba(58,130,246,0.15)' : 'rgba(239,68,68,0.15)',
            color: group === 'include' ? '#bfdbfe' : '#fecaca',
          }}
        >
          <span>{`${fieldLabel(condition.field)}: ${condition.value}`}</span>
          <button
            className="rounded-sm p-0.5 hover:bg-black/20"
            onClick={() => onRemove(index)}
            aria-label={`Fjern ${groupLabel(group)}-filter`}
          >
            <X size={11} />
          </button>
        </span>
      ))}
    </div>
  );
}

interface FilterEditorProps {
  targetGroup: TargetGroup;
  onGroupChange: (group: TargetGroup) => void;
  field: FilterCondition['field'];
  onFieldChange: (field: FilterCondition['field']) => void;
  value: string;
  onValueChange: (value: string) => void;
  onAdd: () => void;
  canAdd: boolean;
}

function FilterEditor({
  targetGroup,
  onGroupChange,
  field,
  onFieldChange,
  value,
  onValueChange,
  onAdd,
  canAdd,
}: FilterEditorProps) {
  const selectClass =
    'flex-1 rounded border bg-surface-overlay px-2 py-1.5 text-[11px] text-text-primary outline-none';
  const selectStyle = {
    borderColor: 'rgba(255,255,255,0.18)',
    background: 'var(--color-surface-overlay)',
  };

  return (
    <div
      className="absolute right-4 top-11 z-20 flex w-72 flex-col gap-3 rounded-lg border p-4 shadow-lg"
      style={{ borderColor: 'rgba(255,255,255,0.18)', background: 'var(--color-surface-raised)' }}
    >
      <p className="text-[11px] font-semibold text-text-primary">Legg til filter</p>
      <div className="flex items-center gap-2">
        <select
          className={selectClass}
          style={selectStyle}
          value={targetGroup}
          onChange={(event) => onGroupChange(event.target.value as TargetGroup)}
        >
          <option value="include">Inkluder</option>
          <option value="exclude">Ekskluder</option>
        </select>
        <select
          className={selectClass}
          style={selectStyle}
          value={field}
          onChange={(event) => onFieldChange(event.target.value as FilterCondition['field'])}
        >
          <option value="sender">Avsender</option>
          <option value="text">Tekst</option>
        </select>
      </div>
      <input
        className="w-full rounded border bg-transparent px-2 py-1.5 text-xs text-text-primary outline-none placeholder:text-text-secondary focus:border-accent/50"
        style={{ borderColor: 'rgba(255,255,255,0.18)' }}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        placeholder={field === 'sender' ? 'epost@eksempel.no' : 'Søketermer'}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          onAdd();
        }}
      />
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-text-secondary">{groupHint(targetGroup)}</span>
        <button
          className="rounded-md px-3 py-1.5 text-[11px] font-semibold disabled:opacity-40"
          style={{
            background: 'rgba(99,102,241,0.2)',
            border: '1px solid rgba(99,102,241,0.4)',
            color: '#a5b4fc',
          }}
          onClick={onAdd}
          disabled={!canAdd}
        >
          Legg til
        </button>
      </div>
    </div>
  );
}
